import React, { useCallback, useReducer } from "react";
import AuthContext from "./AuthContext";
import AuthReducer from "./AuthReducer";
import MethodGet, { MethodPost, MethodPut } from "../../config/Service";
import headerConfig from "../../config/imageHeaders";
import Swal from "sweetalert2";

/**Importar componente token headers */
import tokenAuth from "../../config/TokenAuth";

import { SHOW_ERRORS_API, types } from "../../types";

const AuthState = (props) => {
  // State inicial
  const initialState = {
    token: localStorage.getItem("token"),
    autenticado: !!localStorage.getItem("token"),
    usuario: {},
    cargando: false,
    success: false,
    directions: [],
    puntos: 0,
  };

  const [state, dispatch] = useReducer(AuthReducer, initialState);

  // Función para obtener el usuario autenticado
  const usuarioAutenticado = async () => {
    const token = localStorage.getItem("token");

    if (token) {
      tokenAuth(token);
    }

    try {
      const { data } = await MethodGet("/clientAuth");
      dispatch({
        type: types.OBTENER_USUARIO,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: types.LOGIN_ERROR,
      });
    }
  };

  // Registration — Step 1: Submit data, server validates and sends WhatsApp code
  const submitRegistration = async (data) => {
    const url = "/distribucion/auth/register";
    try {
      const { data: response } = await MethodPost(url, data);
      // Server validated data and sent WhatsApp code, status = "pending"
      return { success: true, message: response.message || "Código enviado por WhatsApp." };
    } catch (error) {
      const msg = error.response?.data?.message || "Ocurrió un error al registrar.";
      const status = error.response?.status;
      if (status === 429) return { success: false, message: msg, rateLimit: true };
      Swal.fire({
        title: "Error",
        text: msg,
        icon: "error",
        showConfirmButton: false,
      });
      dispatch({ type: SHOW_ERRORS_API });
      return { success: false, message: msg };
    }
  };

  // Registration — Step 2: Verify code, server creates user and returns token
  const verifyRegistration = async (data) => {
    const url = "/distribucion/auth/register";
    try {
      const { data: response } = await MethodPost(url, data);

      // Save token and log in
      localStorage.setItem("token", response.token);
      tokenAuth(response.token);

      dispatch({
        type: types.REGISTRO_EXITOSO,
        payload: response,
      });

      Swal.fire({
        title: "¡Registro exitoso!",
        text: "Tu cuenta ha sido verificada y creada correctamente.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      usuarioAutenticado();
      return { success: true };
    } catch (error) {
      const msg = error.response?.data?.message || "Código inválido o expirado.";
      const status = error.response?.status;
      if (status === 429) return { success: false, message: msg, rateLimit: true };
      return { success: false, message: msg };
    }
  };

  // Función para iniciar sesión
  const iniciarSesion = async (datos) => {
    const url = "/loginMerry";
    try {
      const { data: response } = await MethodPost(url, datos);

      // Guardar token en localStorage
      localStorage.setItem("token", response.token);

      // Establecer token en el header
      tokenAuth(response.token);

      dispatch({
        type: types.LOGIN_EXITOSO,
        payload: response,
      });

      // Obtener datos del usuario autenticado
      usuarioAutenticado();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Ocurrió un error al iniciar sesión.",
        icon: "error",
        showConfirmButton: false,
      });

      dispatch({
        type: SHOW_ERRORS_API,
      });
    }
  };
  // OTP Password Reset — Step 1: Request code via WhatsApp
  const requestResetCode = async (numberPhone) => {
    const url = "/distmerry/password/request-reset";
    try {
      const { data } = await MethodPost(url, { number_phone: numberPhone });
      return { success: true, message: data.message || "Código enviado por WhatsApp." };
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || "No se pudo enviar el código. Intenta de nuevo.";
      if (status === 429) return { success: false, message: msg, rateLimit: true };
      return { success: false, message: msg };
    }
  };

  // OTP Password Reset — Step 1b: Request code via Email
  const requestResetByEmail = async (email) => {
    const url = "/distmerry/password/request-reset-email";
    try {
      const { data } = await MethodPost(url, { email });
      return { success: true, message: data.message || "Código enviado a tu correo electrónico." };
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || "No se pudo enviar el código. Verifica tu correo.";
      if (status === 429) return { success: false, message: msg, rateLimit: true };
      return { success: false, message: msg };
    }
  };

  // OTP Password Reset — Step 2: Verify code (supports number_phone or email)
  const verifyResetCode = async (identifier, code) => {
    const url = "/distmerry/password/verify-code";
    try {
      const { data } = await MethodPost(url, { ...identifier, code });
      return { success: true, message: data.message || "Código verificado." };
    } catch (error) {
      const msg = error.response?.data?.message || "Código inválido o expirado.";
      return { success: false, message: msg };
    }
  };

  // OTP Password Reset — Step 3: Reset password
  const resetPasswordOtp = async (formData) => {
    const url = "/distmerry/password/reset";
    try {
      const { data } = await MethodPost(url, formData);
      // Close session so user logs in with the new password
      localStorage.removeItem("token");
      dispatch({ type: types.CERRAR_SESION });
      Swal.fire({
        title: "¡Contraseña actualizada!",
        text: "Tu contraseña se ha restablecido. Inicia sesión con tu nueva contraseña.",
        icon: "success",
        timer: 2500,
        showConfirmButton: false,
      });
      return { success: true, message: data.message || "Contraseña restablecida." };
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || "No se pudo restablecer la contraseña.";
      if (status === 429) {
        return { success: false, message: msg, rateLimit: true };
      }
      Swal.fire({ title: "Error", text: msg, icon: "error" });
      dispatch({ type: SHOW_ERRORS_API });
      return { success: false, message: msg };
    }
  };

  // Función para cerrar sesión
  const cerrarSesion = () => {
    localStorage.removeItem("token");
    dispatch({
      type: types.CERRAR_SESION,
    });
  };

  const eliminarCuenta = (id) => {
    Swal.fire({
      title: "Eliminar mi cuenta",
      allowOutsideClick: false,
      html: `
      <label>Ingresa el texto <b>Eliminar mi cuenta</b></label>
      <input type="text" id="delete" class="swal2-input" placeholder="Eliminar mi cuenta">`,
      confirmButtonText: "Confirmar",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      preConfirm: () => {
        var delete_account = Swal.getPopup().querySelector("#delete").value;
        if (!delete_account) {
          Swal.showValidationMessage(
            `Por favor ingresa ingresa el texto para confirmar`
          );
        } else if (delete_account !== "Eliminar mi cuenta") {
          Swal.showValidationMessage(
            `El texto ingresado debe ser igual a Eliminar mi cuenta`
          );
        }
        return { delete_account: delete_account };
      },
    }).then((result) => {
      if (result.value) {
        // console.log(result.value);
        // delete_account = result.value.delete_account;
        let url = `/cliente/eliminar/${id}`;
        const formData = new FormData();
        formData.append("argument", result.value.delete_account);
        MethodPost(url, formData)
          .then((res) => {
            Swal.fire({
              icon: "success",
              title: `Eliminado `,
              text: "Su cuenta se ha borrado correctamente!",
              timer: 1500,
              showConfirmButton: false,
            });
            cerrarSesion();
            // dispatch({
            //   type: INCREASE_STOCK_PRODUCT,
            //   payload: res.data,
            // });
          })
          .catch((error) => {
            Swal.fire({
              Title: "Error",
              icon: "error",
              text: error.response.data.message,
              timer: 1500,
              showConfirmButton: false,
            });
          });
      }
    });
  };
  const saveFiscalData = (data) => {
    let url = `/distribucion/taxData/${data.id}`;
    MethodPost(url, data)
      .then((res) => {
        dispatch({
          type: types.SAVE_FISCAL_DATA,
          payload: res.data,
        });
        Swal.fire({
          title: "Datos actualizados",
          text: "la información se guardo correctamente",
          timer: 2500,
          icon: "success",
          showConfirmButton: false,
        });
      })
      .catch((error) => {
        console.log(error.response.data);
      });
  };
  const getFiscalData = useCallback(async (id) => {
    const url = `/distribucion/ShowTax/${id}`;
    try {
      const { data } = await MethodGet(url);
      return data;
    } catch (error) {
      console.error(error.response?.data || error.message);
      return null;
    }
  }, []);

  const saveBirthDate = (data) => {
    let url = `/distribucion/save-birthday/${data.id}`;
    console.log(url, "la url");

    const formData = new FormData();
    formData.append("birthdate", data.birthdate);
    MethodPost(url, formData)
      .then((res) => {
        console.log(res, "la respuesta");

        dispatch({
          type: types.SAVE_BIRTHDATE,
          payload: res.data.client,
        });
        Swal.fire({
          title: "Datos actualizados",
          text: "la información se guardo correctamente",
          timer: 2500,
          icon: "success",
          showConfirmButton: false,
        });
      })
      .catch((error) => {
        console.log(error, "el error");

        console.log(error.response.data);
      });
  };
  const AddProfileImage = (data) => {
    Swal.fire({
      title: "Agregar Imagen",
      text: "¿Estas seguro de Agregar esta imagen como foto de perfil?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, Aceptar",
      cancelButtonText: "No, cancelar",
    }).then((result) => {
      if (result.value) {
        const formData = new FormData();
        formData.append("profile_image", data.image);
        let url = `/distribucion/upload-profile-image/${data.client_id}`;
        Swal.fire({
          title: "Subiendo imagen...!",
          showConfirmButton: false,
          showLoading: true,
        });
        Swal.showLoading();
        MethodPost(url, formData, { headerConfig })
          .then((res) => {
            Swal.fire({
              title: "Agregado",
              text: "La imagen se ha agregado correctamente!",
              showConfirmButton: false,
              timer: 1000,
              icon: "success",
            });
            // setTimeout(() => {
            //   window.location.href = "/PropertiesPending";
            // }, 1000);
            dispatch({
              type: types.UPLOAD_PROFILE_IMAGE,
              payload: res.data.client,
            });
          })
          .catch((error) => {
            Swal.fire({
              title: "Error",
              icon: "error",
              text: error.response.data.message,
            });
          });
      }
    });
  };
  const updateUser = (data) => {
    dispatch({
      type: types.OBTENER_USUARIO,
      payload: data,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        autenticado: state.autenticado,
        usuario: state.usuario,
        cargando: state.cargando,
        success: state.success,
        directions: state.directions,
        submitRegistration,
        verifyRegistration,
        iniciarSesion,
        cerrarSesion,
        usuarioAutenticado,
        eliminarCuenta,
        requestResetCode,
        requestResetByEmail,
        verifyResetCode,
        resetPasswordOtp,
        saveFiscalData,
        getFiscalData,
        saveBirthDate,
        AddProfileImage,
        updateUser,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthState;
